import React, { useState, useCallback } from "react";
import { Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import FrameCard from "../../shared/FrameCard";
import HeaderSection from "../HeaderSection/HeaderSection";
import SectionTitle from "../../shared/SectionTitle";
import NoticeBanner from "../../shared/NoticeBanner";
import ActionButton from "../../shared/ActionButton";
import { PROJECT_COLOR, PROJECT_COLOR_DARK } from "../../../constants/colors";
import SkillSheetMainList from "./SkillSheetMainList";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import {
  newSkillSheetPathFor,
  editSkillSheetPathFor,
  deleteSkillSheetPathFor,
} from "../../utils/skillSheetRouting";

/**
 * プロジェクト詳細
 */
const SkillSheetMainSection = ({
  workHistories: initialWorkHistories,
  showEditButton,
  owner,
  onDeleted,
}) => {
  const navigate = useNavigate();

  // 一覧を即時更新したいので state 管理
  const [workHistories, setWorkHistories] = useState(
    initialWorkHistories || [],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // 追加画面へ遷移
  const onAddClick = () => {
    navigate(newSkillSheetPathFor(owner));
  };

  // 編集画面へ遷移
  const onEdit = (workHistoryId) => {
    navigate(editSkillSheetPathFor(workHistoryId, owner));
  };

  // 削除クリック → モーダルを開く
  const onDelete = useCallback((workHistoryId) => {
    setDeleteTargetId(workHistoryId);
    setModalOpen(true);
  }, []);

  // 削除実行（確定）
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTargetId) return;

    try {
      const csrfToken = document.querySelector(
        "meta[name='csrf-token']",
      )?.content;

      // helper で生成
      const basePath = deleteSkillSheetPathFor(deleteTargetId, owner);

      const res = await fetch(`${basePath}.json`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "same-origin",
      });

      if (!res.ok) {
        throw new Error(`Delete failed: ${res.status}`);
      }

      // 即時反映
      setWorkHistories((prev) =>
        prev.filter((workHistory) => workHistory.id !== deleteTargetId),
      );

      // 他コンポーネント（SkillSection / Sidebar）にも反映させる
      if (onDeleted) await onDeleted();

      // モーダル閉じる
      setModalOpen(false);
      setDeleteTargetId(null);
      toast.success("プロジェクトを削除しました。");
    } catch (e) {
      console.error(e);
      toast.error("削除に失敗しました。時間をおいて再度お試しください。");
    }
  }, [deleteTargetId, owner, onDeleted]);

  // キャンセル
  const handleCancelDelete = useCallback(() => {
    setModalOpen(false);
    setDeleteTargetId(null);
  }, []);

  return (
    <FrameCard>
      <Stack spacing={2}>
        <HeaderSection
          title={<SectionTitle title="プロジェクト詳細" />}
          notice={
            showEditButton ? (
              <NoticeBanner text={"直近の数値入り実績で強みを証明します。"} />
            ) : null
          }
          rightActions={
            showEditButton ? (
              <ActionButton
                label="プロジェクトを追加"
                icon={<EditIcon sx={{ color: PROJECT_COLOR }} />}
                onClick={onAddClick}
                sx={{
                  backgroundColor: PROJECT_COLOR,
                  "&:hover": { backgroundColor: PROJECT_COLOR_DARK },
                }}
              />
            ) : null
          }
        />

        <SkillSheetMainList
          workHistories={workHistories}
          onEdit={onEdit}
          onDelete={onDelete}
          showEditButton={showEditButton}
        />

        {/* 確認モーダル */}
        <ConfirmStopModal
          open={modalOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="プロジェクトを削除"
          message="プロジェクトを削除すると元に戻せません。"
          confirmLabel="削除する"
        />
      </Stack>
    </FrameCard>
  );
};

export default SkillSheetMainSection;
